import { useCallback, useEffect, useRef, useState } from 'react';

import type { ColumnFilterCategory, ColumnHeaderFilter } from '../types';

import {
  buildFilterFromEditor,
  readOperatorFromFilter,
  type RowFilterOperator,
} from './filter-operators';

/**
 * Stan edytora komórki wiersza filtrów: operator + wartości (pojedyncza dla
 * warunku, para dla „pomiędzy"), z warunkowym debounce i resyncem.
 *
 * Reguły (R1/R2 z planu):
 *  - Wpisywanie wartości commituje się z debounce 300 ms TYLKO w trybie
 *    serwerowym (`debounced` = obecność `onHeaderFiltersChange`); w trybie
 *    lokalnym commit jest synchroniczny (kontrakt testów, które asertują filtr
 *    natychmiast po wpisaniu, bez przewijania czasu).
 *  - Zmiana operatora commituje się NATYCHMIAST (bez debounce) — istniejąca
 *    wartość jest od razu re-aplikowana; sam wybór operatora bez wartości
 *    kasuje wpis (`buildFilterFromEditor` → null).
 *  - Przejście do/z „pomiędzy" przenosi pierwszą wartość: wartość warunku →
 *    dolna granica zakresu i odwrotnie.
 *  - RESYNC: gdy filtr kolumny zmieni się Z ZEWNĄTRZ (popup nagłówka,
 *    „Wyczyść wszystko", reset strony) — odróżniamy to od echa naszego własnego
 *    commitu po `lastEmitted` — stan edytora jest odtwarzany z nowego filtra.
 */

interface DraftState {
  operator: RowFilterOperator;
  value: string;
  rangeMin: string;
  rangeMax: string;
}

export interface UseFilterRowDraftParams {
  /** Identyfikator kolumny (trafia do `field` filtra). */
  field: string;
  category: ColumnFilterCategory;
  /** Zapisany filtr kolumny (`headerFilters[columnId]`). */
  filter: ColumnHeaderFilter | undefined;
  onApply: (filter: ColumnHeaderFilter | null) => void;
  /** Czy commit wartości ma być odroczony (tryb serwerowy). */
  debounced: boolean;
  delayMs?: number;
}

export interface UseFilterRowDraftResult {
  operator: RowFilterOperator;
  value: string;
  rangeMin: string;
  rangeMax: string;
  setOperator: (op: RowFilterOperator) => void;
  setValue: (v: string) => void;
  setRangeMin: (v: string) => void;
  setRangeMax: (v: string) => void;
  /** Kasuje filtr kolumny (pozycja „Wyczyść" w menu operatorów). */
  clear: () => void;
}

function deriveState(
  filter: ColumnHeaderFilter | undefined,
  category: ColumnFilterCategory,
): DraftState {
  return {
    operator: readOperatorFromFilter(filter, category),
    value: filter?.mode === 'condition' ? (filter.conditionValue ?? '') : '',
    rangeMin: filter?.mode === 'range' ? (filter.rangeMin ?? '') : '',
    rangeMax: filter?.mode === 'range' ? (filter.rangeMax ?? '') : '',
  };
}

const DEFAULT_DELAY_MS = 300;

export function useFilterRowDraft({
  field,
  category,
  filter,
  onApply,
  debounced,
  delayMs = DEFAULT_DELAY_MS,
}: UseFilterRowDraftParams): UseFilterRowDraftResult {
  const [state, setState] = useState<DraftState>(() => deriveState(filter, category));
  const stateRef = useRef(state);
  stateRef.current = state;

  // Ostatni filtr, jaki SAMI wyemitowaliśmy — do odróżnienia echa (parent
  // odsyła nam z powrotem nasz filtr) od realnej zmiany zewnętrznej.
  const lastEmitted = useRef<ColumnHeaderFilter | null | undefined>(filter);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => () => clearTimer(), [clearTimer]);

  const commit = useCallback(
    (next: DraftState, immediate: boolean) => {
      const built = buildFilterFromEditor({
        field,
        operator: next.operator,
        value: next.value,
        rangeMin: next.rangeMin,
        rangeMax: next.rangeMax,
      });
      const fire = () => {
        lastEmitted.current = built;
        onApply(built);
      };
      clearTimer();
      if (immediate || !debounced) {
        fire();
      } else {
        timer.current = setTimeout(fire, delayMs);
      }
    },
    [field, onApply, debounced, delayMs, clearTimer],
  );

  const change = useCallback(
    (patch: Partial<DraftState>, immediate: boolean) => {
      const next = { ...stateRef.current, ...patch };
      stateRef.current = next;
      setState(next);
      commit(next, immediate);
    },
    [commit],
  );

  // Resync ze zmiany zewnętrznej (nie-echo).
  useEffect(() => {
    const incoming = filter ?? null;
    const emitted = lastEmitted.current ?? null;
    if (JSON.stringify(incoming) === JSON.stringify(emitted)) return;
    const derived = deriveState(filter, category);
    stateRef.current = derived;
    setState(derived);
    lastEmitted.current = filter;
    clearTimer();
  }, [filter, category, clearTimer]);

  const setValue = useCallback((v: string) => change({ value: v }, false), [change]);
  const setRangeMin = useCallback((v: string) => change({ rangeMin: v }, false), [change]);
  const setRangeMax = useCallback((v: string) => change({ rangeMax: v }, false), [change]);

  const setOperator = useCallback(
    (op: RowFilterOperator) => {
      const prev = stateRef.current;
      const patch: Partial<DraftState> = { operator: op };
      // Przeniesienie pierwszej wartości przy wejściu/wyjściu z „pomiędzy".
      if (op === 'between' && prev.operator !== 'between') {
        patch.rangeMin = prev.rangeMin || prev.value;
        patch.value = '';
      } else if (op !== 'between' && prev.operator === 'between') {
        patch.value = prev.value || prev.rangeMin;
        patch.rangeMin = '';
        patch.rangeMax = '';
      }
      change(patch, true);
    },
    [change],
  );

  const clear = useCallback(() => {
    const derived = deriveState(undefined, category);
    stateRef.current = derived;
    setState(derived);
    lastEmitted.current = null;
    clearTimer();
    onApply(null);
  }, [category, clearTimer, onApply]);

  return {
    operator: state.operator,
    value: state.value,
    rangeMin: state.rangeMin,
    rangeMax: state.rangeMax,
    setOperator,
    setValue,
    setRangeMin,
    setRangeMax,
    clear,
  };
}
